import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/takeUntil';
import { CurrencyCode, CurrencyCodes, SalaryContext, MonthlyBonus, PayrollMonthlyRate } from '../../../models/payroll-currency.model';
import { ReportType } from '../../../models/report-type.model';
import { ReportDatepickerComponent } from '../../../modals/report-datepicker.component';
import {
  isPlanfixDealPayroll,
  isFinitePayrollNumber,
  PAYROLL_CALCULATION_METHOD,
  resolvePayrollCalculationMethod,
  UserPayrollSettingsResponse
} from '../../../models/deal-payroll.model';
import {dealPayrollGradeKey} from '../../../models/deal-payroll-presentation';
import { EmployeeProbationResponse, UserProfile } from './view-user-profile.types';
import { AppConstants } from './app.constants';
import { ChangeSalaryComponent } from './change-salary/change-salary.component';
import {
  INTERNAL_CALCULATION_LABEL_KEY,
  PLANFIX_CALCULATION_LABEL_KEY,
  PLANFIX_DEAL_CALCULATION_LABEL_KEY,
  PROFILE_EMPTY_TEXT,
  PROFILE_LINE_BREAK,
  PROFILE_PROBATION_MESSAGE_KEYS
} from './view-user-profile.constants';
import { ViewUserProfileFeedbackService } from './view-user-profile-feedback.service';
import { ViewUserProfilePayrollGateway } from './view-user-profile-payroll.gateway';
import {GradePromotionEvent} from '../../common/grade-growth/grade-growth.model';

@Injectable()
export class ViewUserProfilePayrollFacade implements OnDestroy {
  salaryMonthlyRate: PayrollMonthlyRate = null;
  gradeMonthlyRate: PayrollMonthlyRate = null;
  salaryCurrency: CurrencyCode = CurrencyCodes.BYN;
  monthlyBonus: MonthlyBonus = null;
  gradeMonthlySalary: number | null = null;
  payrollCurrency: CurrencyCode = null;
  gradeLabelKey: string = null;
  private salaryContextUser: string = null;
  private salaryContextRequest: Subscription;
  probation: EmployeeProbationResponse;
  isProbationLoaded = false;
  probationError = false;
  probationPending = false;
  private destroy$ = new Subject<void>();

  constructor(
    private gateway: ViewUserProfilePayrollGateway,
    private feedback: ViewUserProfileFeedbackService,
    private modalService: NgbModal
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get promotionCommitted$(): Observable<GradePromotionEvent> { return this.gateway.promotionCommitted$; }

  loadSalaryContext(userId: string, user: UserProfile): void {
    if (this.salaryContextRequest) { this.salaryContextRequest.unsubscribe(); }
    this.salaryContextUser = userId;
    this.monthlyBonus = null;
    this.salaryMonthlyRate = null;
    this.gradeMonthlyRate = null;
    this.gradeMonthlySalary = null;
    this.payrollCurrency = null;
    this.gradeLabelKey = null;
    this.salaryContextRequest = this.gateway.salaryContext(userId).takeUntil(this.destroy$).subscribe(
      (context: SalaryContext) => this.applySalaryContext(userId, user, context),
      () => this.salaryCurrency = CurrencyCodes.BYN
    );
  }

  private applySalaryContext(userId: string, user: UserProfile, context: SalaryContext): void {
    if (this.salaryContextUser === userId && user.id === userId && context.userId === userId) {
      user.salary = context.salary;
      user.descriptionSalary = context.description;
      this.salaryCurrency = context.effectiveCurrency;
      this.monthlyBonus = context.monthlyBonus || null;
      this.salaryMonthlyRate = context.salaryMonthlyRate || null;
      this.gradeMonthlyRate = context.gradeMonthlyRate || null;
      this.gradeMonthlySalary = isFinitePayrollNumber(context.gradeMonthlySalary) ? context.gradeMonthlySalary : null;
      this.payrollCurrency = context.payrollCurrency || null;
      this.gradeLabelKey = context.gradeCode ? dealPayrollGradeKey(context.gradeCode) : null;
    }
  }

  openEmployment(userId: string, user: UserProfile): void {
    const modalRef = this.modalService.open(ReportDatepickerComponent);
    modalRef.componentInstance.reportType = ReportType.BY_PROFILE;
    modalRef.componentInstance.userId = userId;
    modalRef.result.then((result) => {
      if (result) {
        user.dateOfEmployment = result;
      }
    });
  }

  openSalary(user: UserProfile, isAdmin: boolean, onSuccess: () => void): void {
    const modalRef = this.modalService.open(ChangeSalaryComponent);
    modalRef.componentInstance.setId(user.id);
    modalRef.componentInstance.setValue(user.salary);
    modalRef.componentInstance.setAdmin(isAdmin);
    modalRef.result.then((result) => {
      if (result === AppConstants.MODAL_RESULT.OK) {
        onSuccess();
      }
    }, () => null);
  }

  isDealMethod(user: UserProfile): boolean {
    return isPlanfixDealPayroll(user);
  }

  calculationLabelKey(user: UserProfile): string {
    const method = resolvePayrollCalculationMethod(user);
    if (method === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL) {
      return PLANFIX_DEAL_CALCULATION_LABEL_KEY;
    }
    return method === PAYROLL_CALCULATION_METHOD.PLANFIX_HOURLY
      ? PLANFIX_CALCULATION_LABEL_KEY
      : INTERNAL_CALCULATION_LABEL_KEY;
  }

  applySettings(
    user: UserProfile,
    settings: UserPayrollSettingsResponse,
    currentHourlyValue: boolean
  ): boolean {
    if (!user || !settings || settings.userId !== user.id) {
      return currentHourlyValue;
    }
    user.payrollCalculationMethod = settings.payrollCalculationMethod;
    user.payrollMethod = settings.payrollMethod;
    return settings.payrollCalculationMethod === PAYROLL_CALCULATION_METHOD.PLANFIX_HOURLY;
  }

  loadProbation(userId: string): void {
    this.isProbationLoaded = false;
    this.probationError = false;
    this.gateway.probation(userId).takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<EmployeeProbationResponse>) => {
        this.probation = response.body;
        this.isProbationLoaded = true;
      },
      () => {
        this.probationError = true;
        this.isProbationLoaded = true;
      }
    );
  }

  approveProbation(userId: string): void {
    if (!this.probation || !this.probation.canApprove || this.probationPending) {
      return;
    }
    this.probationPending = true;
    this.gateway.approveProbation(userId).takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<EmployeeProbationResponse>) => {
        this.probation = response.body;
        this.probationPending = false;
      },
      () => this.handleProbationError()
    );
  }

  probationTooltip(): string {
    if (!this.probation) {
      return PROFILE_EMPTY_TEXT;
    }
    const tooltipParts = [
      this.feedback.text(PROFILE_PROBATION_MESSAGE_KEYS.TOOLTIP_DESCRIPTION)
    ];
    if (!this.probation.approved && !this.probation.canApprove
      && (!this.probation.blockers || !this.probation.blockers.length)) {
      tooltipParts.push(this.feedback.text(PROFILE_PROBATION_MESSAGE_KEYS.PERMISSION_REQUIRED));
    }
    if (this.probation.blockers && this.probation.blockers.length) {
      tooltipParts.push(this.probation.blockers
        .map((blocker) => this.probationBlockerText(blocker))
        .join(PROFILE_LINE_BREAK));
    }
    return tooltipParts.join(PROFILE_LINE_BREAK);
  }

  probationBlockerText(blocker: string): string {
    return this.feedback.text(blocker, {
      date: this.probation ? this.probation.eligibleAt : PROFILE_EMPTY_TEXT
    });
  }

  private handleProbationError(): void {
    this.probationPending = false;
    this.feedback.error(PROFILE_PROBATION_MESSAGE_KEYS.UPDATE_ERROR);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/view-user-profile-payroll.facade.ts